import { AccountStatus } from '../account-deletion';

export interface AuthenticationResult {
  accessToken: string;
  expiresIn: number;
  idToken: string;
  refreshToken: string;
  tokenType: string;
}

/**
 * Detecção de uso simultâneo da mesma conta. Só vem preenchido quando houve detecção —
 * ausente é o caso normal. É um aviso, não um bloqueio: o login conclui normalmente e o
 * usuário decide o que fazer. Ver ANGULAR_SESSION_ID_CONTROLE_DISPOSITIVOS.md (ftcloud-api).
 */
export interface AccountSharingAlert {
  otherActiveSessions: number;
  /** ISO 3166-1 alpha-2 do último acesso das outras sessões ("AR"). É o que permite ao
   *  usuário reconhecer (ou não) o acesso. Pode não vir. */
  lastSeenCountry?: string | null;
}

export interface LoginResponse {
  authenticationResult: AuthenticationResult | null;
  challengeParameters: Record<string, string> | null;
  challengeName: string | null;
  session: string | null;
  sessionId?: string | null;
  /**
   * Só vem preenchido no POST /global/oauth/callback (login social). "AUTHENTICATED" é o
   * login concluído de sempre; "REGISTRATION_REQUIRED" indica identidade validada no
   * provedor (Google/Apple) mas sem conta na plataforma — ver email/name abaixo.
   * Ver docs/integracoes/frontend-angular/ANGULAR_SOCIAL_REGISTRATION_REQUIRED.md (ftcloud-api).
   */
  status?: 'AUTHENTICATED' | 'REGISTRATION_REQUIRED' | string;
  /** Preenchido junto com status === "REGISTRATION_REQUIRED". Sempre vem quando presente. */
  email?: string | null;
  /** Preenchido junto com status === "REGISTRATION_REQUIRED". Pode vir nulo. */
  name?: string | null;
  /**
   * Status da waitlist ('pending' | 'approved' | 'rejected'). Vem no
   * POST /global/handoff/exchange, e alimenta o AccessBlockService.setFromStatus() —
   * permite bloquear o shell já no boot, sem esperar a primeira volta do /user/active.
   */
  approvalStatus?: string | null;
  /**
   * Aviso de acesso simultâneo. Vem em `/global/login`, `/global/respond-challenge` e
   * `/global/oauth/callback` quando há outras sessões ativas — ver AccountSharingAlert.
   */
  accountSharingAlert?: AccountSharingAlert | null;
  /**
   * `true` = o código do desafio estava ERRADO e ainda há tentativa. A resposta de
   * retentativa tem formato idêntico à do primeiro desafio (mesmo `challengeName`, mesmo
   * shape, `session` novo) — sem este flag as duas seriam indistinguíveis, e a tela
   * pediria "digite o código enviado para seu e-mail" para alguém que não vai receber
   * e-mail nenhum: numa retentativa o código continua o mesmo.
   *
   * Opcional porque `LoginResponse` também serve login, refresh, handoff e social, onde
   * não se aplica. Ver ftcloud-api/docs/handoff-front-web-mfa-retentativa-otp.md.
   */
  challengeRetry?: boolean;
  /**
   * Tentativas restantes. Pode vir `null` quando o Cognito não informa — por isso NÃO
   * decide nada, só alimenta o texto. Quem decide a retentativa é o `challengeRetry`.
   */
  attemptsRemaining?: number | null;
  /**
   * Estado da conta (exclusão agendada). Só tem significado quando `authenticationResult` vem
   * preenchido: o desafio de MFA e o REGISTRATION_REQUIRED trazem `'active'` por padrão e NÃO
   * devem ser usados para rotear; o /global/refresh não traz estes campos.
   * Ver AccountDeletionService.applyLoginResponse().
   */
  accountStatus?: AccountStatus;
  /** Fim da carência (ISO-8601 UTC). Só preenchido com `accountStatus === 'pending_deletion'`. */
  deletionScheduledAt?: string | null;
}

/** Desfechos de `POST /global/respond-challenge`, na ordem em que o contrato os avalia. */
export type ChallengeOutcome = 'retry' | 'authenticated' | 'unresolved';

/**
 * Classifica a resposta do desafio. `challengeRetry` vem ANTES de `authenticationResult`
 * de propósito: é o único sinal que separa "errou, tenta de novo" do primeiro desafio, e
 * ler `attemptsRemaining` para isso quebraria quando o Cognito o omite.
 */
export function challengeOutcome(response: LoginResponse | null | undefined): ChallengeOutcome {
  if (response?.challengeRetry) return 'retry';
  if (response?.authenticationResult) return 'authenticated';
  return 'unresolved';
}

export interface CodeDeliveryDetails {
  destination: string;
  deliveryMedium: string;
  attributeName: string;
}

export interface CognitoApiResponse {
  userConfirmed: boolean | null;
  userSub: string | null;
  codeDeliveryDetails: CodeDeliveryDetails | null;
  /**
   * `true` em /confirm-signup e /resend-code quando a conta já estava ativada antes da chamada
   * (ex.: scanner de links do e-mail abriu o link de ativação antes do usuário). Não houve
   * confirmação nem reenvio: basta mandar o usuário ao login.
   */
  alreadyConfirmed?: boolean;
}

export interface UserAttribute {
  name: string;
  value: string;
}

export interface GetUserResponse {
  username: string;
  userAttributes: UserAttribute[];
  userMFASettingList: any[];
  preferredMfaSetting: string | null;
}

export interface AuthApiError {
  error: string;
  details: string;
}
